import { FC, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DAYJS_DATE_FORMAT } from '$constants/datetimes';
import { PositionFilters } from '$constants/PositionFilters';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { ReactComponent as Star } from '$icons/uncolored/filledStar.svg';
import { ReactComponent as Eye } from '$icons/EyeGreen.svg';
import { ReactComponent as EyeOff } from '$icons/GreenEyeOff.svg';
import StatusCandidatePosition from '$types/serverTypes/StatusCandidatePosition';
import { Tooltip } from 'primereact/tooltip';

import PositionInternalDto from '../../../types/serverTypes/positionInternalDto';
import classes from '../PositionPage.module.css';
import PositionStatus from '../../../constants/PositionStatus';
import PositionCardMenu from '../PositionCardMenu/PositionCardMenu';
import PositionDto from '../../../types/serverTypes/positionDto';
import { useOriginPath } from '../../../hooks/use-origin-path';
import SPKJobModal, { SPKJobModalActionsTypes } from '../SPKJobModal';

interface PositionCardInternalProps {
  position: PositionDto | PositionInternalDto;
  onDuplicate: () => void;
}

const PositionCardInternal: FC<PositionCardInternalProps> = ({
  position,
  onDuplicate,
}) => {
  const [t] = useTranslation('global');
  const { setOriginPath } = useOriginPath();
  const dictionary: {
    [key: string]: string;
  } = {
    Open: t('PositionCard.Open'),
    Closed: t('PositionCard.Filled'),
    Deactivated: t('PositionCard.Deactivated'),
  };

  const chipClass =
    position.Status !== PositionStatus.Closed &&
    position.Status !== PositionStatus.Deactivated
      ? classes.ChipStatus
      : classes.ChipStatusClose;

  const [openModal, setOpenModal] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const card = useRef<HTMLDivElement>(null);
  const MAX_SKILLS = 5;
  const [searchParams] = useSearchParams();

  const mostRecentAddedCandidate: string = useMemo(() => {
    return (
      position?.CandidatesPositionStatus?.reduce((prev, act) => {
        if (!prev) return `${act.CreateTime}` as string;

        if (dayjs(prev).isBefore(act.CreateTime))
          return `${act.CreateTime}` as string;
        return prev;
      }, '') || ''
    );
  }, [position]);

  const handleCloseModal = () => {
    setOpenModal(false);
    setIsDuplicating(false);
  };

  const candidatesStatusAmmount = useMemo(() => {
    let groupedStatusAmmounts = {
      [StatusCandidatePosition.new]: 0,
      [StatusCandidatePosition.shortlisted]: 0,
      [StatusCandidatePosition.requestInterview]: 0,
      [StatusCandidatePosition.interested]: 0,
      [StatusCandidatePosition.declined]: 0,
    } as Record<StatusCandidatePosition, number>;
    if (
      position &&
      position.CandidatesPositionStatus &&
      position.CandidatesPositionStatus.length > 0
    ) {
      groupedStatusAmmounts = position.CandidatesPositionStatus.reduce(
        (prev, act) => {
          if (Number.isFinite(prev[act.Status])) {
            prev[act.Status] += 1;
          }
          return prev;
        },
        groupedStatusAmmounts
      );
    }
    return groupedStatusAmmounts;
  }, [position]);

  return (
    <>
      <div
        ref={card}
        className={classes.card}
        data-testid="positionCard"
        tabIndex={-1}
      >
        <div className={`${classes.cardHeader} flex items-start`}>
          <div className={classes.cardJob}>
            <p className={classes.cardJobCategory}>
              {`${position?.Company?.Name}  ID:${position?.Id ?? ''}`}
            </p>
            <p className={classes.cardJobTitle}>
              {position.EnableSmartInterviews && (
                <Star className="fill-internal-main inline-block items-center mb-1 mr-2" />
              )}
              {position.CustomJobTitle || position?.JobPosition?.title}{' '}
            </p>
            {mostRecentAddedCandidate &&
              searchParams.get('sortBy') === PositionFilters.CandidateAdded && (
                <p className="text-xs">
                  Last Added:{' '}
                  <span className="font-semibold">
                    {dayjs(mostRecentAddedCandidate).format(DAYJS_DATE_FORMAT)}
                  </span>
                </p>
              )}
            {mostRecentAddedCandidate &&
              searchParams.get('sortBy') === PositionFilters.Created && (
                <p className="text-xs">
                  Last Created:{' '}
                  <span className="font-semibold">
                    {dayjs(position.CreateTime).format(DAYJS_DATE_FORMAT)}
                  </span>
                </p>
              )}
          </div>
          <div className={classes.RightContent}>
            <div className={classes.PositionStatus}>
              <div className="flex items-center gap-3">
                {position.JobDescriptionFileUrl && (
                  <a
                    href={position.JobDescriptionFileUrl}
                    title="Download Job Description File"
                    target="_blank"
                    aria-label="Download Job Description File"
                    rel="noreferrer"
                    className="text-internal-main"
                  >
                    <i className="pi pi-download" />
                  </a>
                )}
                {position.IsPublic ? (
                  <Eye
                    className="inline-block items-center mb-1 mr-2"
                    title="Public Position"
                  />
                ) : (
                  <EyeOff
                    className="inline-block items-center mb-1 mr-2"
                    title="Is not Public Position"
                  />
                )}
                <div className={chipClass}>
                  {(position.Status !== undefined &&
                    dictionary[position.Status]) ||
                    t('PositionCard.Open')}
                </div>
              </div>
              <p className={`${classes.tooltipCandidateText} text-nowrap`}>
                {`${position?.CandidatesPositionStatus?.length} ${t(
                  'SimeraInternal.Candidates'
                )}`}
              </p>
              <Tooltip
                target={`.position_${position.Id}_status_tooltip`}
                position="top"
                pt={{
                  root: {
                    className: 'pt-2',
                  },
                }}
              >
                <div className="text-sm">
                  <p>
                    {t('ChipGroup.New')}: {candidatesStatusAmmount.new}
                  </p>
                  <p>
                    {t('ChipGroup.ShortList')}:{' '}
                    {candidatesStatusAmmount.shortlisted}
                  </p>
                  <p>
                    {t('ChipGroup.BeingInterviewed')}:{' '}
                    {candidatesStatusAmmount.requestInterview}
                  </p>
                  <p>
                    {t('ChipGroup.Interested')}:{' '}
                    {candidatesStatusAmmount.interested}
                  </p>
                  <p>
                    {t('ChipGroup.Inactive')}:{' '}
                    {candidatesStatusAmmount.declined}
                  </p>
                </div>
              </Tooltip>
              <div
                className={`flex flex-row gap-2 position_${position.Id}_status_tooltip`}
              >
                {Object.entries(candidatesStatusAmmount).map(([key, value]) => {
                  return (
                    <p
                      className="bg-internal-main/25 rounded px-1 text-xs"
                      key={`position_${position.Id}_${key}`}
                    >
                      {value}
                    </p>
                  );
                })}
              </div>
            </div>
            <PositionCardMenu
              isInternal
              position={position}
              setOpenEditModal={setOpenModal}
              handleOpenDuplicateModal={() => {
                setIsDuplicating(true);
                setOpenModal(true);
              }}
            />
          </div>
        </div>
        <div className={classes.divider} />
        <div className={classes.cardBody}>
          <div className={classes.experience}>
            <p className={classes.cardBodySubtitle}>{`${t(
              'EmployerPositions.Experience'
            )}:`}</p>
            <p
              className={classes.cardBodyText}
            >{`${position.ExperienceYears} years+`}</p>
          </div>
          <div className={classes.salary}>
            <p className={classes.cardBodySubtitle}>{`${t(
              'EmployerAddPosition.PositionSalary'
            )}:`}</p>
            <p
              className={classes.cardBodyText}
            >{`$${position.SalaryLowerBound} - $${position.SalaryUpperBound}`}</p>
          </div>
        </div>
        <div className={classes.cardSkills}>
          <p className={classes.cardBodySubtitle}>{`${t(
            'EmployerAddPosition.PositionSkills'
          )}:`}</p>
          <div className={classes.chipContainer}>
            {position.Skills_position?.slice(0, MAX_SKILLS).map((skill) => (
              <div
                className={classes.Chip}
                key={`${skill.Title}-${position.Id}`}
              >
                <p className={classes.ChipText}> {skill.Title} </p>
              </div>
            ))}
          </div>
        </div>
        <div className={classes.cardCandidates}>
          <button
            type="button"
            tabIndex={0}
            className={classes.cardLink}
            aria-hidden
          >
            <Link
              to={`/simera-dashboard/positions/${position.Id}`}
              className={classes.Link}
              onClick={() => {
                setOriginPath(window.location.pathname);
              }}
            >
              {t('SimeraInternal.PositionDetails')}
            </Link>
          </button>
        </div>
      </div>
      <ModalInTailwind isOpen={openModal} closeModal={handleCloseModal}>
        <SPKJobModal
          position={position}
          closeHandler={handleCloseModal}
          onDuplicate={onDuplicate}
          action={
            isDuplicating
              ? SPKJobModalActionsTypes.duplicate
              : SPKJobModalActionsTypes.edit
          }
        />
      </ModalInTailwind>
    </>
  );
};

export default PositionCardInternal;
